import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ButtonStateService from "@core-ui/ContextServices/ButtonStateService/ButtonStateService";
import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import ModalToOpen from "@core-ui/ContextServices/ModalToOpenService/model/ModalsToOpen";
import { RequestStatus, useApi } from "@core-ui/hooks/useApi";
import useMediaQuery from "@core-ui/hooks/useMediaQuery";
import { cssMedia } from "@core-ui/utils/cssUtils";
import BaseRightExtensions from "@ext/articleProvider/components/BaseRightExtensions";
import {
	ProviderPickerItem,
	ProviderPickerLoading,
} from "@ext/articleProvider/components/ProviderPicker/ProviderPickerItem";
import type { ProviderItemProps } from "@ext/articleProvider/models/types";
import t from "@ext/localization/locale/translate";
import type { FragmentAlreadyUseWarnProps } from "@ext/markdown/elements/fragment/edit/components/FragmentAlreadyUseWarn";
import { FRAGMENTS_PANEL_ID } from "@ext/markdown/elements/fragment/edit/components/FragmentsPanel/types/constants";
import FragmentUpdateService from "@ext/markdown/elements/fragment/edit/components/FragmentUpdateService";
import FragmentService from "@ext/markdown/elements/fragment/edit/components/Tab/FragmentService";
import FragmentUsages from "@ext/markdown/elements/fragment/edit/components/Tab/FragmentUsages";
import type { FragmentReference, FragmentRenderData } from "@ext/markdown/elements/fragment/edit/model/types";
import getFragmentScope from "@ext/markdown/elements/fragment/logic/getFragmentScope";
import { getEditorContext } from "@ext/markdown/elementsUtils/editorContext/EditorContext";
import { WorkspaceToolsManage, WorkspaceToolsPicker } from "@ext/workspaceTools/components"; // workspace-tools seam
import type { Editor } from "@tiptap/core";
import {
	DropdownEmpty,
	DropdownMenuItem,
	DropdownMenuSearchItem,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	useSearchableMenu,
} from "@ui-kit/Dropdown";
import { usePanelToggle } from "@ui-kit/FloatingPanel";
import { Icon } from "@ui-kit/Icon";
import { useCallback, useMemo, useRef, useState } from "react";

interface FragmentsButtonProps {
	editor: Editor;
}

const FragmentsButton = ({ editor }: FragmentsButtonProps) => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const [fragmentsList, setFragmentsList] = useState<ProviderItemProps[]>([]);
	const reopenFragmentIdRef = useRef<string>(null);
	const { selectedID } = FragmentService.value;
	const panelTriggerRef = useRef<HTMLDivElement>(null);
	const { toggle: toggleFragmentsPanel } = usePanelToggle(FRAGMENTS_PANEL_ID, panelTriggerRef);
	const isMobile = useMediaQuery(cssMedia.JSnarrow);

	const { call: getFragments, status } = useApi<ProviderItemProps[]>({
		url: (api) => api.getArticleListInGramaxDir("fragment"),
		onDone: (data) => {
			setFragmentsList(data);
			FragmentService.setItems(data);

			const fragmentId = reopenFragmentIdRef.current;
			if (!fragmentId) return;
			reopenFragmentIdRef.current = null;
			const fragment = data.find((item) => item.id === fragmentId);
			if (fragment) FragmentService.openItem(fragment);
		},
		parse: "json",
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: it's ok
	const insertFragment = useCallback(
		async (ref: FragmentReference) => {
			const res = await FetchService.fetch<FragmentRenderData>(
				apiUrlCreator.getFragmentRenderData(ref.id, getFragmentScope(ref.type)),
			);
			if (!res.ok || !editor || editor.isDestroyed) return;

			const data = await res.json();
			editor
				.chain()
				.setFragment({ ...data, ...ref })
				.focus(editor.state.selection.anchor)
				.run();
		},
		[editor, apiUrlCreator],
	);

	const { disabled: actionDisabled } = ButtonStateService.useCurrentAction({ action: "fragment" });
	const disabled = actionDisabled || !!getEditorContext(editor).noWrite;

	const onEditClick = useCallback((fragment: ProviderItemProps) => {
		FragmentService.openItem(fragment);
	}, []);

	const onDelete = useCallback(
		(id: string) => {
			if (selectedID === id) {
				void FetchService.fetch(apiUrlCreator.clearArticlesContentWithFragment(id));
				FragmentService.closeItem();
			}

			setFragmentsList((items) => {
				const nextItems = items.filter((item) => item.id !== id);
				FragmentService.setItems(nextItems);
				return nextItems;
			});
			FragmentUpdateService.clearContent(id);
		},
		[selectedID],
	);

	const onMarkdownChange = useCallback(
		async (id: string) => {
			await FragmentUpdateService.updateContent(id, apiUrlCreator);
			if (selectedID === id) {
				reopenFragmentIdRef.current = id;
				FragmentService.closeItem();
			}
			void getFragments();
		},
		[selectedID, getFragments],
	);

	const preDelete = useCallback(async (id: string) => {
		return new Promise<boolean>((resolve) => {
			ModalToOpenService.setValue<FragmentAlreadyUseWarnProps>(ModalToOpen.FragmentAlreadyUseWarn, {
				fragmentId: id,
				onClose: () => {
					resolve(false);
					ModalToOpenService.resetValue();
				},
				onSubmit: () => {
					resolve(true);
					ModalToOpenService.resetValue();
				},
			});
		});
	}, []);
	const {
		search,
		setSearch,
		contentRef,
		inputRef,
		handleContentKeyDown,
		handleInputKeyDown,
		handleFocusOutside,
		filterItems,
	} = useSearchableMenu();

	const onOpenChange = useCallback(
		(open: boolean) => {
			if (!open) return setSearch("");
			if (status !== RequestStatus.Loading) void getFragments();
		},
		[getFragments, status, setSearch],
	);

	const filteredFragments = useMemo(
		() => filterItems(fragmentsList.map((fragment) => ({ ...fragment, label: fragment.title }))),
		[fragmentsList, filterItems],
	);

	const isReady = status === RequestStatus.Ready;
	const hasFragments = fragmentsList.length > 0;

	return (
		<DropdownMenuSub onOpenChange={onOpenChange}>
			<DropdownMenuSubTrigger disabled={disabled}>
				<div className="flex items-center gap-2" data-qa="qa-fragments" data-testid="fragments-menu">
					<Icon icon="square-dashed-bottom" />
					{t("fragments")}
				</div>
			</DropdownMenuSubTrigger>
			<DropdownMenuSubContent
				alignOffset={!isMobile ? -18 : 0}
				className="flex max-w-[min(20rem,var(--radix-dropdown-menu-content-available-width,100%))] flex-col"
				onFocusOutside={handleFocusOutside}
				onKeyDown={handleContentKeyDown}
				ref={contentRef}
				sideOffset={!isMobile ? 2 : 6}
			>
				<DropdownMenuSearchItem
					onChange={(e) => setSearch(e.target.value)}
					onClick={(e) => e.stopPropagation()}
					onKeyDown={handleInputKeyDown}
					placeholder={`${t("find2")} ${t("fragment").toLowerCase()}`}
					ref={inputRef}
					value={search}
				/>
				<DropdownMenuSeparator />
				{/* workspace-tools seam */}
				<WorkspaceToolsPicker
					catalogEmpty={isReady && !hasFragments}
					catalogItems={
						!isReady ? (
							<ProviderPickerLoading />
						) : (
							hasFragments &&
							(filteredFragments.length === 0 ? (
								<DropdownEmpty>{t("list.no-results-found")}</DropdownEmpty>
							) : (
								filteredFragments.map((fragment) => (
									<ProviderPickerItem
										key={fragment.id}
										onSelect={() => void insertFragment({ id: fragment.id })}
										title={fragment.title}
									>
										<BaseRightExtensions
											id={fragment.id}
											items={(id) => (
												<FragmentUsages
													fragmentId={id}
													isSubmenu
													trigger={
														<>
															<Icon icon="file-symlink" />
															{t("view-usage")}
														</>
													}
												/>
											)}
											onDelete={onDelete}
											onEdit={() => onEditClick(fragment)}
											onMarkdownChange={onMarkdownChange}
											preDelete={preDelete}
											providerType="fragment"
										/>
									</ProviderPickerItem>
								))
							))
						)
					}
					catalogNotFound={isReady && hasFragments && filteredFragments.length === 0}
					onSelect={(ref) => void insertFragment(ref)}
					query={search}
					type="fragment"
				/>
				<div className="mt-auto">
					{/* workspace-tools seam */}
					<WorkspaceToolsManage
						catalogItem={(tools) => (
							<>
								{(hasFragments || tools) && <DropdownMenuSeparator />}
								<DropdownMenuItem
									aria-describedby={tools?.describedBy}
									onSelect={toggleFragmentsPanel}
									textValue="manage-fragments"
								>
									<div
										className="flex w-full min-w-0 items-center gap-2"
										data-testid="manage-fragments"
										ref={panelTriggerRef}
									>
										<Icon icon="settings" />
										{t("fragments-panel-manage")}
										{tools?.indicator}
									</div>
								</DropdownMenuItem>
							</>
						)}
					/>
				</div>
			</DropdownMenuSubContent>
		</DropdownMenuSub>
	);
};

export default FragmentsButton;
